import { create } from 'zustand';
import type { GameId } from '../api/types';
import { DEFAULT_GAME_ID, normalizeGameId } from '../data/games';
import type { RuleId, RuleSchemaEntry } from '../data/ruleSchema';
import type { RulePreset } from '../data/lotteryData';
import type { TicketRule } from '../types/rules';
import { buildRuleFromPreset, createDefaultPresets, createDefaultRules } from '../data/ruleBuilderSeed';
import { RULE_SCHEMA } from '../data/ruleSchema';
import {
  defaultRuleGameConfig,
  RULE_CONFIG_VERSION,
  sanitizeRuleConfig,
  sanitizeTicketRules,
  type PersistedRuleConfig,
  type PersistedRuleGameConfig,
} from '../rules/ruleConfigModel';
import type { SimpleVibeId } from '../rules/simpleVibeStacks';

export interface RuleBuilderState {
  activeGameId: GameId;
  byGame: Partial<Record<GameId, PersistedRuleGameConfig>>;
  rulePresets: RulePreset[];
  /** Server-backed schema for supported rules; merged with local `RULE_SCHEMA` at read time. */
  ruleSchemaOverlay: Partial<Record<RuleId, RuleSchemaEntry>>;
  simpleVibe: SimpleVibeId;
  rules: TicketRule[];
  /** Cleared only if you need to block UI until `hydrate()` completes. */
  hasHydrated: boolean;
  setActiveGame: (gameId: GameId) => void;
  setSimpleVibe: (vibe: SimpleVibeId) => void;
  addRule: (id: RuleId) => void;
  removeRule: (instanceId: string) => void;
  updateWeight: (instanceId: string, weight: number) => void;
  updateRule: (instanceId: string, patch: Partial<TicketRule>) => void;
  hydrateRuleConfig: (config: PersistedRuleConfig, options?: { activeGameId?: GameId }) => void;
  exportRuleConfig: () => PersistedRuleConfig;
  /** Replace builder state (e.g. after remote sync). */
  hydrate: (payload: {
    rulePresets?: RulePreset[];
    rules?: TicketRule[];
    ruleSchemaOverlay?: Partial<Record<RuleId, RuleSchemaEntry>>;
  }) => void;
}

function currentGameConfig(state: Pick<RuleBuilderState, 'rules' | 'simpleVibe'>): PersistedRuleGameConfig {
  return {
    rules: state.rules,
    simpleVibe: state.simpleVibe,
  };
}

function patchActiveGame(state: RuleBuilderState, nextGame: PersistedRuleGameConfig): Partial<RuleBuilderState> {
  return {
    byGame: {
      ...state.byGame,
      [state.activeGameId]: nextGame,
    },
    rules: nextGame.rules,
    simpleVibe: nextGame.simpleVibe,
  };
}

function staticSnapshot(): Pick<
  RuleBuilderState,
  'activeGameId' | 'byGame' | 'rulePresets' | 'rules' | 'ruleSchemaOverlay' | 'simpleVibe'
> {
  const initialGame = {
    ...defaultRuleGameConfig(),
    rules: createDefaultRules(),
  };
  return {
    activeGameId: DEFAULT_GAME_ID,
    byGame: { [DEFAULT_GAME_ID]: initialGame },
    rulePresets: createDefaultPresets(),
    rules: initialGame.rules,
    ruleSchemaOverlay: {},
    simpleVibe: initialGame.simpleVibe,
  };
}

export const useRuleBuilderStore = create<RuleBuilderState>((set, get) => ({
  ...staticSnapshot(),
  /** Static catalog is usable immediately; signed-in users refresh from the API. */
  hasHydrated: true,
  setActiveGame: (gameId) =>
    set((state) => {
      const activeGameId = normalizeGameId(gameId);
      const byGame = {
        ...state.byGame,
        [state.activeGameId]: currentGameConfig(state),
      };
      const nextGame = byGame[activeGameId] ?? defaultRuleGameConfig();
      return {
        activeGameId,
        byGame,
        rules: nextGame.rules,
        simpleVibe: nextGame.simpleVibe,
      };
    }),
  setSimpleVibe: (simpleVibe) =>
    set((state) => patchActiveGame(state, { ...currentGameConfig(state), simpleVibe })),
  addRule: (id) => {
    set((state) => {
      if (state.rules.some((r) => r.id === id)) return {};
      const preset = state.rulePresets.find((p) => p.id === id);
      if (!preset) return {};
      const schema = state.ruleSchemaOverlay[id] ?? RULE_SCHEMA[id];
      return patchActiveGame(state, {
        ...currentGameConfig(state),
        rules: [...state.rules, buildRuleFromPreset(id, preset, schema)],
      });
    });
  },
  removeRule: (instanceId) =>
    set((state) =>
      patchActiveGame(state, {
        ...currentGameConfig(state),
        rules: state.rules.filter((r) => r.instanceId !== instanceId),
      })
    ),
  updateWeight: (instanceId, weight) =>
    set((state) =>
      patchActiveGame(state, {
        ...currentGameConfig(state),
        rules: sanitizeTicketRules(state.rules.map((r) => (r.instanceId === instanceId ? { ...r, weight } : r))),
      })
    ),
  updateRule: (instanceId, patch) =>
    set((state) =>
      patchActiveGame(state, {
        ...currentGameConfig(state),
        rules: sanitizeTicketRules(state.rules.map((r) => (r.instanceId === instanceId ? { ...r, ...patch } : r))),
      })
    ),
  hydrateRuleConfig: (config, options) =>
    set((state) => {
      const sane = sanitizeRuleConfig(config);
      const activeGameId = normalizeGameId(options?.activeGameId ?? state.activeGameId);
      const nextGame = sane.byGame[activeGameId] ?? defaultRuleGameConfig();
      return {
        activeGameId,
        byGame: sane.byGame,
        rules: nextGame.rules,
        simpleVibe: nextGame.simpleVibe,
        hasHydrated: true,
      };
    }),
  exportRuleConfig: () => {
    const state = get();
    return sanitizeRuleConfig({
      version: RULE_CONFIG_VERSION,
      byGame: {
        ...state.byGame,
        [state.activeGameId]: currentGameConfig(state),
      },
    });
  },
  hydrate: (payload) =>
    set((state) => ({
      ...(payload.rulePresets !== undefined ? { rulePresets: payload.rulePresets } : {}),
      ...(payload.ruleSchemaOverlay !== undefined ? { ruleSchemaOverlay: payload.ruleSchemaOverlay } : {}),
      ...(payload.rules !== undefined
        ? patchActiveGame(state, {
            ...currentGameConfig(state),
            rules: sanitizeTicketRules(payload.rules),
          })
        : {}),
      hasHydrated: true,
    })),
}));

/** Non-hook access for services (same store instance). */
export function getRuleBuilderState(): RuleBuilderState {
  return useRuleBuilderStore.getState();
}

export function resetRuleBuilderToStaticSeed(): void {
  useRuleBuilderStore.setState({ ...staticSnapshot(), hasHydrated: true });
}
